import Axios, {AxiosResponse} from "axios";
import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState,} from "react";
import _ from "lodash";
import {useBoolean} from "@fluentui/react-hooks";
import {IModelError} from "../interfaces/IModelError";
import {TenantResource} from "../enums/TenantResource";
import {FormAction, TWithStoragesAndOriginals} from "../types/TenantTypes";

export interface IUseTenantResourceSingle<DatabaseModel, DtoModel, TReturn> {
    dto?: DtoModel &
        Partial<DatabaseModel> &
        TWithStoragesAndOriginals<DtoModel & Partial<DatabaseModel>>;
    setDto: Dispatch<
        SetStateAction<
            | (DtoModel &
            Partial<DatabaseModel> &
            TWithStoragesAndOriginals<DtoModel & Partial<DatabaseModel>>)
            | undefined
        >
    >;
    save: (additionalProperties?: any) => Promise<TReturn>;
    dtoErrors?: IModelError<DtoModel>;
    setModel: (d?: Partial<TReturn>) => void;
    deleteModel: (override?: TReturn) => Promise<void>;
    model?: Partial<TReturn>;
    loadModel: (id: any) => Promise<TReturn>;
    dtoChanged: boolean;
    action: <TData, TResponse>(
        route: string,
        data?: TData,
        params?: any,
    ) => Promise<AxiosResponse<TResponse>>;
    setDtoErrors: (v?: IModelError<DtoModel>) => void;
    getId?: (data: any) => any;
    loading?: boolean;
    modifyDtoBeforeSave?: (dto: DtoModel) => DtoModel;
}

export type TBeforeSubmitFormCallback<TDto, TReturn> = (
    original: Partial<TReturn> | undefined,
    dto: TDto,
    resolve: (value: TReturn | PromiseLike<TReturn>) => void,
    reject: (reason?: any) => void,
    setDtoErrors: Dispatch<SetStateAction<IModelError<TDto> | undefined>>,
) => Promise<boolean>;

export const useTenantResourceSingleParametrised = <
    DatabaseModel,
    DtoModel,
    TReturn,
>({
      resource,
      keepModel,
      defaultValues,
      ifShowingTutorial,
      getId,
      additionalIntParameter,
      additionalStringParameter,
      afterFormAction,
      modifyDtoBeforeSave,
      beforeSubmitForm,
  }: {
    resource: TenantResource;
    keepModel?: boolean;
    defaultValues?: Partial<DtoModel>;
    ifShowingTutorial?: Partial<DtoModel>;
    getId?: (data: any) => any;
    additionalIntParameter?: number;
    additionalStringParameter?: string;
    afterFormAction?: (formAction: FormAction, dto: TReturn) => void;
    modifyDtoBeforeSave?: (dto: DtoModel) => DtoModel;
    beforeSubmitForm?: TBeforeSubmitFormCallback<DtoModel, TReturn>;
}): IUseTenantResourceSingle<DatabaseModel, DtoModel, TReturn> =>
    useTenantResourceSingle<DatabaseModel, DtoModel, TReturn>(
        resource,
        keepModel,
        defaultValues,
        getId,
        additionalIntParameter,
        additionalStringParameter,
        afterFormAction,
        modifyDtoBeforeSave,
        beforeSubmitForm,
    );

export const useTenantResourceSingle = <DatabaseModel, DtoModel, TReturn>(
    resource: TenantResource,
    keepModel: boolean = true,
    defaultValues?: Partial<DtoModel>,
    getId: (data: any) => any = (data) => data["id"],
    additionalIntParameter?: number,
    additionalStringParameter?: string,
    afterFormAction?: (formAction: FormAction, dto: TReturn) => void,
    modifyDtoBeforeSave?: (dto: DtoModel) => DtoModel,
    beforeSubmitForm?: TBeforeSubmitFormCallback<DtoModel, TReturn>, // boolean vrati kao continue?
): IUseTenantResourceSingle<DatabaseModel, DtoModel, TReturn> => {
    const [dto, setDto] = useState<
        DtoModel &
        Partial<DatabaseModel> &
        TWithStoragesAndOriginals<DtoModel & Partial<DatabaseModel>>
    >();
    const [dtoErrors, setDtoErrors] = useState<IModelError<DtoModel>>();
    const [model, setModel] = useState<Partial<TReturn>>();
    const dtoChanged = useMemo(
        () => !!model && !!dto && !_.isEqual(dto, model),
        [dto, model],
    );
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);

    const loadModel = useCallback(
        (id: any) =>
            new Promise<TReturn>((resolve, reject) => {
                setLoading();
                setDtoErrors(undefined);
                Axios.get<TReturn>(resource.split("+")[0] + "/" + id, {
                    params: {additionalIntParameter, additionalStringParameter},
                })
                    .then(({data}) => {
                        setModel(data);
                        resolve(data);
                    })
                    .catch(({response: {data}}) => {
                        reject(data);
                    })
                    .finally(setNotLoading);
            }),
        [additionalIntParameter, additionalStringParameter],
    );

    const save = useCallback(
        (additionaProperties: any) =>
            new Promise<TReturn>(async (resolve, reject) => {
                let finalDto = {...dto, ...additionaProperties};
                if (modifyDtoBeforeSave) {
                    finalDto = modifyDtoBeforeSave(finalDto);
                }
                if (
                    !beforeSubmitForm ||
                    (await beforeSubmitForm(
                        model,
                        finalDto,
                        resolve,
                        reject,
                        setDtoErrors,
                    ))
                ) {
                    setLoading();
                    setDtoErrors(undefined);
                    if (model && getId(model)) {
                        Axios.put<TReturn>(
                            resource.split("+")[0] + "/" + getId(model),
                            finalDto,
                            {params: {additionalIntParameter, additionalStringParameter}},
                        )
                            .then(({data}) => {
                                setDtoErrors(undefined);
                                const next = (data ?? finalDto) as unknown as TReturn;
                                if (keepModel) setModel(next);
                                resolve(next);
                                if (afterFormAction) {
                                    afterFormAction("PUT", next);
                                }
                            })
                            .catch(({response: {data}}) => {
                                reject(data);
                                setDtoErrors(data);
                            })
                            .finally(setNotLoading);
                    } else {
                        Axios.post<TReturn>(resource.split("+")[0], finalDto, {
                            params: {additionalIntParameter, additionalStringParameter},
                        })
                            .then(({data}) => {
                                setDtoErrors(undefined);
                                const next = (data ?? finalDto) as unknown as TReturn;
                                if (keepModel) setModel(next);
                                resolve(next);
                                if (afterFormAction) {
                                    afterFormAction("POST", next);
                                }
                            })
                            .catch(({response: {data}}) => {
                                reject(data);
                                setDtoErrors(data);
                            })
                            .finally(setNotLoading);
                    }
                }
            }),
        [
            dto,
            model,
            keepModel,
            getId,
            afterFormAction,
            additionalIntParameter,
            additionalStringParameter,
            modifyDtoBeforeSave,
            beforeSubmitForm,
        ],
    );

    const deleteModel = useCallback(
        (override?: TReturn) =>
            new Promise<void>((resolve, reject) => {
                const data = override ?? model;
                if (data) {
                    setLoading();
                    Axios.delete(resource.split("+")[0] + "/" + getId(data), {
                        params: {additionalIntParameter, additionalStringParameter},
                    })
                        .then(() => {
                            setDtoErrors(undefined);
                            resolve();
                            if (afterFormAction) {
                                afterFormAction("DELETE", data as any);
                            }
                        })
                        .catch(reject)
                        .finally(setNotLoading);
                } else reject();
            }),
        [resource, model, getId, additionalIntParameter, additionalStringParameter],
    );

    useEffect(() => {
        if (model && getId(model)) {
            setDto(model as any);
        } else {
            setDto({...model, ...defaultValues} as any);
        }
    }, [model]);

    const action = useCallback(
        <TData, TResponse>(a: string, d?: TData, p?: any) =>
            new Promise<AxiosResponse<TResponse>>((resolve, reject) => {
                setLoading();
                const endpoint = `${resource.split("+")[0]}/${a}`;
                Axios.post(endpoint, d, {params: p})
                    .then(({data}) => {
                        console.error(data);
                        resolve(data);
                    })
                    .catch(reject)
                    .finally(setNotLoading);
            }),
        [resource],
    );

    return {
        loadModel,
        dto,
        setDto,
        save,
        dtoErrors,
        setModel,
        deleteModel,
        model,
        dtoChanged,
        action,
        setDtoErrors,
        loading,
    };
};
